"use client"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { usePassphrase } from "@/lib/PassphraseProvider"
import { generateSecretToken } from "@/utils/crypto"
import { AlertTriangle, Copy, Eye, EyeOff, Shield } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import ConfirmModal from "@/components/common/ConfirmModal"

const STORAGE_KEY = "passhprase"

interface PassphraseGeneratorProps {
  onPassphraseSaved?: () => void
  onPassphraseCleared?: () => void
  suppressGlobalEnforcement?: boolean
  confirmModalClassName?: string
}

export function PassphraseGenerator({
  onPassphraseSaved,
  onPassphraseCleared,
  suppressGlobalEnforcement,
  confirmModalClassName,
}: PassphraseGeneratorProps) {
  const { setExternalFlowActive } = usePassphrase()
  const [passphrase, setPassphrase] = useState<string>("")
  const [savedPassphrase, setSavedPassphrase] = useState<string | null>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [isSavedVisible, setIsSavedVisible] = useState(false)
  const { t } = useTranslation()

  // Load saved passphrase on mount (migrate from old key if present)
  useEffect(() => {
    if (suppressGlobalEnforcement) {
      setExternalFlowActive(true)
    }
    const existing =
      localStorage.getItem(STORAGE_KEY) ||
      localStorage.getItem("user-passphrase")
    if (existing) {
      setSavedPassphrase(existing)
      setPassphrase(existing)
    }
    return () => {
      if (suppressGlobalEnforcement) {
        setExternalFlowActive(false)
      }
    }
  }, [suppressGlobalEnforcement, setExternalFlowActive])

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(passphrase)
      toast.success(t("copied_title"), {
        description: t("copied_desc"),
      })
    } catch (err) {
      toast.error(t("copy_failed_title"), {
        description: t("copy_failed_desc"),
      })
    }
  }

  const [isClearOpen, setIsClearOpen] = useState(false)
  const clearPassphrase = () => {
    setIsClearOpen(true)
  }

  const savePassphrase = () => {
    if (!passphrase) {
      toast.error(t("nothing_to_save_title"), {
        description: t("nothing_to_save_desc"),
      })
      return
    }
    localStorage.setItem(STORAGE_KEY, passphrase)
    setSavedPassphrase(passphrase)
    toast.success(t("saved_title"), {
      description: t("saved_desc"),
    })

    // Notify parent component that passphrase was saved
    if (onPassphraseSaved) {
      onPassphraseSaved()
    }
  }

  return (
    <>
      <Card className="mx-auto w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-2 flex items-center justify-center">
            <Shield className="text-primary mr-2 h-8 w-8" />
            <CardTitle className="text-2xl">{t("passphrase")}</CardTitle>
          </div>
          <CardDescription>
            {t("passphrase_description")} {""}
            <Button
              variant="link"
              rel="noopener noreferrer"
              className="cursor-pointer px-0 hover:no-underline"
              onClick={() => {
                setPassphrase(generateSecretToken())
                setIsVisible(true)
              }}
            >
              click
            </Button>
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="relative">
              <Input
                type={isVisible ? "text" : "password"}
                placeholder={t("passphrase_placeholder")}
                autoComplete="nope"
                value={passphrase}
                onChange={(e) => setPassphrase(e.target.value)}
                className="pr-10"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsVisible(!isVisible)}
                className="absolute top-1/2 right-1 -translate-y-1/2"
              >
                {isVisible ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={copyToClipboard}
                variant="outline"
                className="flex-1 bg-transparent"
              >
                <Copy className="mr-2 h-4 w-4" />
                {t("copy")}
              </Button>
              <Button onClick={savePassphrase} className="flex-1">
                {t("save")}
              </Button>
            </div>

            <Button
              disabled={!savedPassphrase}
              onClick={clearPassphrase}
              variant="destructive"
              className="w-full"
              size="sm"
            >
              {t("clear_passphrase")}
            </Button>
          </div>

          <Alert className=" ">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className=" ">
              <strong>{t("important_label")}</strong> {t("important_msg")}
            </AlertDescription>
          </Alert>

          {savedPassphrase && (
            <div className="space-y-2">
              <div className="text-muted-foreground text-sm">
                {t("saved_passphrase_label")}
              </div>
              <div className="bg-muted border-muted-foreground/20 rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <code className="font-mono text-xs break-all">
                    {isSavedVisible
                      ? savedPassphrase
                      : "••••••••••••••••••••••••••••••••"}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsSavedVisible(!isSavedVisible)}
                    className="ml-2 flex-shrink-0"
                  >
                    {isSavedVisible ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="text-muted-foreground space-y-1 text-xs">
            <p>{t("passphrase_bullet_local")}</p>
            <p>{t("passphrase_bullet_use")}</p>
            <p>{t("passphrase_bullet_backup")}</p>
          </div>
        </CardContent>
      </Card>
      <ConfirmModal
        isOpen={isClearOpen}
        onClose={() => setIsClearOpen(false)}
        onConfirm={() => {
          setPassphrase("")
          setIsVisible(false)
          localStorage.removeItem(STORAGE_KEY)
          setSavedPassphrase(null)
          if (onPassphraseCleared) {
            onPassphraseCleared()
          }
        }}
        titleKey="clear_passphrase_prompt"
        destructive
        confirmLabelKey="common.delete"
        contentClassName={confirmModalClassName}
      />
    </>
  )
}
